// Filling in a week (round 44). Built phone first: one card per day, native
// time inputs (16px below sm, so iOS does not zoom), 44px targets, and the
// week's totals in a bar that never scrolls away.
//
// TWO MODES.
//   worker   AUTOSAVES, 800ms after the last change. Their own working copy;
//            nothing they type in a draft is logged.
//   office   An explicit Save. A manager's change to somebody else's week is
//            logged with before and after and the worker reads it, so it is a
//            decision taken once, not a stream of autosaves into the history.
//
// THE AUTOSAVE CONTRACT, which is where Zanetto Tools' timesheet lost work
// twice (an in-flight save's echo overwrote newer typing; a page-hide re-sent
// stale days):
//   1. The page's own state is the source of truth while it is open. A save's
//      response updates the lockVersion and nothing the person can type.
//   2. One save at a time. A change made while one is in flight is sent when
//      it lands, with the lockVersion it returned.
//   3. A change is flushed on leaving: when the tab is hidden, and when this
//      component unmounts (the timer is cleared, then the save is sent).
//   4. A week that would not pass the server's checks is not sent; the day
//      says what is missing instead of an error appearing mid-typing.
//
// The hours shown while typing come from lib/timesheetHours.ts, the server's
// own calculator copied byte for byte, so the total here is the total the
// office approves.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api';
import {
  dayWorkedMinutes,
  finishesNextDay,
  minutesToHm,
  spanMinutes,
  weekTotals,
  type DayHours,
} from '../../lib/timesheetHours';
import { dayLabel, dayOfWeek, weekdayName } from '../../lib/timesheetWeeks';
import {
  timesheets,
  toInput,
  type DayInput,
  type JobOption,
  type TimesheetDetail,
} from '../../timesheetsApi';

const AUTOSAVE_MS = 800;
const BREAK_CHOICES = [0, 15, 30, 45, 60];
const ELSEWHERE = '__elsewhere__';

export type SaveState = 'saved' | 'saving' | 'unsaved' | 'blocked' | 'error';

function hoursOf(day: DayInput): DayHours {
  return {
    startTime: day.startTime,
    finishTime: day.finishTime,
    breakMinutes: day.breakMinutes,
    leaveTypeId: day.leaveTypeId,
    leaveMinutes: day.leaveMinutes,
  };
}

/**
 * The server's checks, said on the day before anything is sent (rule 4).
 * The server checks again; this is so nobody is told off mid-word.
 */
export function dayProblems(day: DayInput): Partial<Record<keyof DayInput, string>> {
  const out: Partial<Record<keyof DayInput, string>> = {};
  const hasStart = day.startTime !== null && day.startTime !== '';
  const hasFinish = day.finishTime !== null && day.finishTime !== '';
  if (hasStart !== hasFinish) {
    out[hasStart ? 'finishTime' : 'startTime'] = hasStart ? 'Add a finish time.' : 'Add a start time.';
  } else if (hasStart && day.startTime === day.finishTime) {
    out.finishTime = 'The start and finish are the same.';
  } else if (hasStart && day.breakMinutes >= spanMinutes(hoursOf(day))) {
    out.breakMinutes = 'The break is as long as the day.';
  }
  if (hasStart && day.leaveTypeId && day.leaveMinutes === null) {
    out.leaveMinutes = 'Hours were worked too, so say how many hours of leave.';
  }
  return out;
}

export function WeekEditor({
  detail,
  mode,
  onSaved,
  onStale,
  onStateChange,
  footer,
}: {
  detail: TimesheetDetail;
  mode: 'worker' | 'office';
  /** Every successful save's answer, for the page to show status and totals. */
  onSaved: (next: TimesheetDetail) => void;
  onStale: () => void;
  onStateChange?: (state: SaveState) => void;
  /** The submit button, drawn in the sticky bar beside the totals. */
  footer: (api: {
    flush: () => Promise<TimesheetDetail | null>;
    days: DayInput[];
    state: SaveState;
  }) => React.ReactNode;
}) {
  const [days, setDays] = useState<DayInput[]>(() => detail.days.map(toInput));
  const [note, setNote] = useState<string>(detail.sheet.note ?? '');
  const [state, setState] = useState<SaveState>('saved');
  const [serverProblems, setServerProblems] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<Set<string>>(() => new Set());
  const [copyNote, setCopyNote] = useState<string | null>(null);

  // Refs, so the timer, the page-hide handler and unmount all see the latest.
  const latest = useRef({ days, note });
  latest.current = { days, note };
  const lockVersion = useRef(detail.sheet.lockVersion);
  const revision = useRef(0);
  const savedRevision = useRef(0);
  const inFlight = useRef<Promise<TimesheetDetail | null> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stale = useRef(false);

  const setSaveState = useCallback(
    (next: SaveState) => {
      setState(next);
      onStateChange?.(next);
    },
    [onStateChange],
  );

  const problems = useMemo(() => days.map(dayProblems), [days]);
  const blocked = problems.some((p) => Object.keys(p).length > 0);

  /** Sends what the page holds now. One at a time (rule 2). */
  const save = useCallback(async (): Promise<TimesheetDetail | null> => {
    if (stale.current) return null;
    if (inFlight.current) {
      await inFlight.current.catch(() => null);
    }
    if (savedRevision.current === revision.current) return null;
    const sending = revision.current;
    const { days: sendDays, note: sendNote } = latest.current;
    if (sendDays.map(dayProblems).some((p) => Object.keys(p).length > 0)) {
      setSaveState('blocked');
      return null;
    }
    setSaveState('saving');
    const request = timesheets
      .saveDays(detail.sheet.id, {
        lockVersion: lockVersion.current,
        days: sendDays.map((d) => ({
          ...d,
          startTime: d.startTime || null,
          finishTime: d.finishTime || null,
        })),
        note: sendNote.trim() === '' ? null : sendNote,
      })
      .then((next) => {
        // Rule 1: the lockVersion moves on; the typing on screen stays.
        lockVersion.current = next.sheet.lockVersion;
        savedRevision.current = Math.max(savedRevision.current, sending);
        setServerProblems({});
        setError(null);
        onSaved(next);
        setSaveState(savedRevision.current === revision.current ? 'saved' : 'unsaved');
        return next;
      })
      .catch((err: unknown) => {
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          (err.details as { code?: string })?.code === 'stale'
        ) {
          stale.current = true;
          onStale();
        } else if (err instanceof ApiError && err.status === 422) {
          setServerProblems(err.fields);
          setError(err.message);
        } else {
          setError(err instanceof ApiError ? err.message : 'Could not save. Check your connection.');
        }
        setSaveState('error');
        return null;
      })
      .finally(() => {
        inFlight.current = null;
      });
    inFlight.current = request;
    const result = await request;
    // Something changed while that was in the air (rule 2).
    if (result && mode === 'worker' && savedRevision.current !== revision.current) {
      return save();
    }
    return result;
  }, [detail.sheet.id, mode, onSaved, onStale, setSaveState]);

  /** Everything pending, now. For submit, page-hide and unmount (rule 3). */
  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    return save();
  }, [save]);

  const changed = useCallback(() => {
    revision.current += 1;
    setSaveState('unsaved');
    if (mode !== 'worker') return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void save();
    }, AUTOSAVE_MS);
  }, [mode, save, setSaveState]);

  useEffect(() => {
    if (mode !== 'worker') return;
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      // Rule 3: leaving the page sends what was typed.
      void flush();
    };
  }, [mode, flush]);

  function update(index: number, patch: Partial<DayInput>) {
    setDays((current) => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    changed();
  }

  function copyFrom(index: number) {
    const before = latest.current.days[index - 1];
    if (!before) return;
    update(index, {
      startTime: before.startTime,
      finishTime: before.finishTime,
      breakMinutes: before.breakMinutes,
      jobId: before.jobId,
      location: before.location,
      allowanceTypeIds: before.allowanceTypeIds,
    });
  }

  const totals = weekTotals(days.map(hoursOf));
  const leaveTypes = detail.leaveTypes;
  const allowanceTypes = detail.allowanceTypes;
  const holidays = new Map(detail.nonWorkingDays.map((d) => [d.date, d.name]));
  const nonWorking = new Set(detail.nonWorkingWeekdays);

  const saveLabel: Record<SaveState, string> = {
    saved: mode === 'worker' ? 'Saved' : 'No unsaved changes',
    saving: 'Saving…',
    unsaved: mode === 'worker' ? 'Saving soon…' : 'Unsaved changes',
    blocked: 'Finish the highlighted day to save',
    error: 'Not saved',
  };

  const isEmpty = (d: DayInput) => !d.startTime && !d.leaveTypeId;
  const anyEmptyWorkday = days.some((d) => isEmpty(d) && !nonWorking.has(dayOfWeek(d.date)));

  /**
   * Last week's times, into the days still empty (round 44). A crew on regular
   * hours fills in the same start, finish and break most weeks; this saves
   * them retyping it, and it is still their week to check and sign. Only
   * EMPTY days are touched, never a day already filled in, and the job is
   * not copied: where somebody was sent changes week to week, and this week's
   * schedule has already put its own job on each day.
   */
  async function copyLastWeek() {
    if (!detail.previousWeekId) return;
    setCopyNote(null);
    try {
      const previous = await timesheets.detail(detail.previousWeekId);
      let copied = 0;
      // Leave is never copied: a day off last week is not a day off this one.
      const next = latest.current.days.map((d, i) => {
        const from = previous.days[i];
        if (!from || !isEmpty(d) || !from.startTime || !from.finishTime) return d;
        copied += 1;
        return {
          ...d,
          startTime: from.startTime,
          finishTime: from.finishTime,
          breakMinutes: from.breakMinutes,
          allowanceTypeIds:
            d.allowanceTypeIds.length > 0
              ? d.allowanceTypeIds
              : from.allowanceTypeIds.filter((id) =>
                  detail.allowanceTypes.some((t) => t.id === id && !t.archived),
                ),
        };
      });
      if (copied > 0) setDays(next);
      setCopyNote(
        copied === 0
          ? 'Nothing to copy: last week had no hours on the days still empty here.'
          : `Copied last week’s times into ${copied} day${copied === 1 ? '' : 's'}. Check them before you sign.`,
      );
      if (copied > 0) changed();
    } catch (err) {
      setCopyNote(err instanceof ApiError ? err.message : 'Could not read last week.');
    }
  }

  return (
    <div>
      {error && (
        <p className="banner-error mb-4" role="alert">
          {error}
        </p>
      )}

      {detail.previousWeekId && anyEmptyWorkday && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <button type="button" className="btn-secondary btn-sm" onClick={() => void copyLastWeek()}>
            Copy last week’s times
          </button>
          <span className="hint !mt-0">Fills the empty days only.</span>
        </div>
      )}
      {copyNote && (
        <p className="banner-info mb-3" role="status">
          {copyNote}
        </p>
      )}

      <ol className="space-y-3" aria-label="Days of the week">
        {days.map((day, index) => {
          const problem = { ...problems[index] };
          for (const [key, message] of Object.entries(serverProblems)) {
            const match = key.match(/^days\.(\d+)\.(\w+)$/);
            if (match && Number(match[1]) === index) problem[match[2] as keyof DayInput] = message;
          }
          const empty =
            !day.startTime &&
            !day.leaveTypeId &&
            day.allowanceTypeIds.length === 0 &&
            !day.note &&
            !day.jobId &&
            !day.location;
          const quiet = nonWorking.has(dayOfWeek(day.date)) && empty && !shown.has(day.date);
          return (
            <li key={day.date}>
              {quiet ? (
                <div className="card flex min-h-11 items-center justify-between gap-3 px-4 py-2">
                  <span className="text-sm text-gray-600">
                    {dayLabel(day.date)}
                    {holidays.get(day.date) && <> · {holidays.get(day.date)}</>}
                  </span>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setShown((s) => new Set(s).add(day.date))}
                  >
                    Add {weekdayName(day.date)}
                  </button>
                </div>
              ) : (
                <DayCard
                  day={day}
                  index={index}
                  problem={problem}
                  holiday={holidays.get(day.date) ?? null}
                  booked={detail.jobOptions.byDate[day.date] ?? []}
                  others={detail.jobOptions.others}
                  stored={detail.days[index]!}
                  leaveTypes={leaveTypes}
                  allowanceTypes={allowanceTypes}
                  onChange={(patch) => update(index, patch)}
                  onCopy={index > 0 ? () => copyFrom(index) : undefined}
                />
              )}
            </li>
          );
        })}
      </ol>

      <section className="panel mt-4">
        <label className="form-label" htmlFor="week-note">
          Note for the week <span className="muted">(optional)</span>
        </label>
        <textarea
          id="week-note"
          className="form-input min-h-20 w-full"
          maxLength={2000}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            changed();
          }}
        />
      </section>

      {/* The bar the totals live in: above the phone tab bar, at the bottom
          of the page on a desktop (FormFillPage's pattern). */}
      <div className="sticky bottom-16 z-20 -mx-4 mt-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur lg:bottom-0">
        {/* One line on a phone: the totals take what is left beside the
            button and wrap inside their own column, never pushing the
            button onto a second line (the bar covers the screen it sits on). */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="!mb-0 text-sm font-semibold text-gray-900 tabular-nums">
              {minutesToHm(totals.workedMinutes)} worked
              {totals.leaveMinutes > 0 && <> · {minutesToHm(totals.leaveMinutes)} leave</>}
              {totals.fullLeaveDays > 0 && (
                <>
                  {' '}
                  · {totals.fullLeaveDays} day{totals.fullLeaveDays === 1 ? '' : 's'} off
                </>
              )}
            </p>
            <p className="!mb-0 text-xs text-gray-500" role="status" aria-live="polite">
              {blocked && state !== 'saving' ? saveLabel.blocked : saveLabel[state]}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {mode === 'office' && (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={state === 'saving' || state === 'saved' || blocked}
                onClick={() => void save()}
              >
                Save changes
              </button>
            )}
            {footer({ flush, days, state })}
          </div>
        </div>
      </div>
    </div>
  );
}

function DayCard({
  day,
  index,
  problem,
  holiday,
  booked,
  others,
  stored,
  leaveTypes,
  allowanceTypes,
  onChange,
  onCopy,
}: {
  day: DayInput;
  index: number;
  problem: Partial<Record<keyof DayInput, string>>;
  holiday: string | null;
  booked: JobOption[];
  others: JobOption[];
  stored: TimesheetDetail['days'][number];
  leaveTypes: TimesheetDetail['leaveTypes'];
  allowanceTypes: TimesheetDetail['allowanceTypes'];
  onChange: (patch: Partial<DayInput>) => void;
  onCopy?: () => void;
}) {
  const id = (field: string) => `day-${index}-${field}`;
  const hours = hoursOf(day);
  const worked =
    day.startTime && day.finishTime && day.startTime !== day.finishTime ? dayWorkedMinutes(hours) : 0;
  // Derived from the day where they can be, so a copied or reloaded day shows
  // the right control; the state only remembers a choice the data cannot
  // show yet ("Other" picked, minutes not typed).
  const [elsewhereChosen, setElsewhere] = useState(false);
  const [otherChosen, setOtherBreak] = useState(false);
  const elsewhere = elsewhereChosen || (day.jobId === null && day.location !== null && day.location !== '');
  const otherBreak = otherChosen || !BREAK_CHOICES.includes(day.breakMinutes);
  const hasWindow = !!day.startTime && !!day.finishTime;
  // Leave, allowances and the note are folded away on a day that uses none
  // of them: most days are a start, a finish, a break and a job, and a crew
  // member should not scroll past three empty sections five times a week.
  const hasExtras =
    !!day.leaveTypeId ||
    day.allowanceTypeIds.length > 0 ||
    !!day.note ||
    !!problem.leaveMinutes ||
    !!problem.leaveTypeId ||
    !!problem.allowanceTypeIds;
  const [moreChosen, setMore] = useState(false);
  const showMore = moreChosen || hasExtras;

  // The job the day already holds, kept on offer even when it is no longer
  // booked or open (a job closed since Friday still names Friday's hours).
  const keep: JobOption | null =
    stored.jobId && stored.jobNumber && ![...booked, ...others].some((j) => j.id === stored.jobId)
      ? {
          id: stored.jobId,
          number: stored.jobNumber,
          title: stored.jobTitle ?? '',
        }
      : null;
  const whereValue = elsewhere ? ELSEWHERE : (day.jobId ?? '');

  const activeLeave = leaveTypes.filter((t) => !t.archived || t.id === day.leaveTypeId);
  const activeAllowances = allowanceTypes.filter((t) => !t.archived || day.allowanceTypeIds.includes(t.id));

  return (
    <section className="card p-4" aria-labelledby={id('heading')}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 id={id('heading')} className="!mb-0 text-base font-semibold text-gray-900">
            {dayLabel(day.date)}
          </h3>
          {holiday && <p className="!mb-0 text-xs text-amber-800">{holiday}</p>}
          {booked.length > 0 && (
            <p className="!mb-0 text-xs text-gray-500">
              Booked: {booked.map((j) => `${j.number} · ${j.title}`).join(', ')}
            </p>
          )}
        </div>
        <p className="!mb-0 text-sm font-semibold tabular-nums text-gray-900" aria-label="Hours worked">
          {worked > 0 ? minutesToHm(worked) : ''}
        </p>
      </div>

      <div className="row pair">
        <label htmlFor={id('start')}>
          Start
          <input
            id={id('start')}
            type="time"
            className="form-input"
            value={day.startTime ?? ''}
            aria-invalid={problem.startTime ? true : undefined}
            onChange={(e) => onChange({ startTime: e.target.value || null })}
          />
        </label>
        <label htmlFor={id('finish')}>
          Finish
          <input
            id={id('finish')}
            type="time"
            className="form-input"
            value={day.finishTime ?? ''}
            aria-invalid={problem.finishTime ? true : undefined}
            onChange={(e) => onChange({ finishTime: e.target.value || null })}
          />
        </label>
      </div>
      {hasWindow && finishesNextDay(hours) && (
        <p className="hint !mt-1">Finishes the next morning. The hours count on {weekdayName(day.date)}.</p>
      )}
      {(problem.startTime || problem.finishTime) && (
        <p className="mt-1 text-sm text-red-700" role="alert">
          {problem.startTime ?? problem.finishTime}
        </p>
      )}

      {hasWindow && (
        <div className="row mt-3">
          <label htmlFor={id('break')}>
            Unpaid break
            <select
              id={id('break')}
              className="form-input"
              value={otherBreak ? 'other' : String(day.breakMinutes)}
              onChange={(e) => {
                if (e.target.value === 'other') {
                  setOtherBreak(true);
                } else {
                  setOtherBreak(false);
                  onChange({ breakMinutes: Number(e.target.value) });
                }
              }}
            >
              {BREAK_CHOICES.map((m) => (
                <option key={m} value={m}>
                  {m === 0 ? 'No break' : `${m} minutes`}
                </option>
              ))}
              <option value="other">Other…</option>
            </select>
          </label>
          {otherBreak && (
            <label htmlFor={id('break-minutes')}>
              Minutes
              <input
                id={id('break-minutes')}
                type="number"
                inputMode="numeric"
                min={0}
                max={600}
                className="form-input"
                value={day.breakMinutes}
                onChange={(e) =>
                  onChange({
                    breakMinutes: Math.max(0, Math.round(Number(e.target.value) || 0)),
                  })
                }
              />
            </label>
          )}
        </div>
      )}
      {problem.breakMinutes && (
        <p className="mt-1 text-sm text-red-700" role="alert">
          {problem.breakMinutes}
        </p>
      )}

      <div className="row mt-3">
        <label htmlFor={id('where')}>
          Job or place
          <select
            id={id('where')}
            className="form-input"
            value={whereValue}
            aria-invalid={problem.jobId ? true : undefined}
            onChange={(e) => {
              const value = e.target.value;
              if (value === ELSEWHERE) {
                setElsewhere(true);
                onChange({ jobId: null });
              } else {
                setElsewhere(false);
                onChange({
                  jobId: value === '' ? null : value,
                  location: null,
                });
              }
            }}
          >
            <option value="">Not set</option>
            {booked.length > 0 && (
              <optgroup label="Booked this day">
                {booked.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.number} · {j.title}
                  </option>
                ))}
              </optgroup>
            )}
            {(others.length > 0 || keep) && (
              <optgroup label="Other jobs">
                {keep && (
                  <option value={keep.id}>
                    {keep.number} · {keep.title}
                  </option>
                )}
                {others.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.number} · {j.title}
                  </option>
                ))}
              </optgroup>
            )}
            {!day.jobId && stored.jobNumber && !stored.jobId && (
              <option value="" disabled>
                {stored.jobNumber} (job removed)
              </option>
            )}
            <option value={ELSEWHERE}>Somewhere else…</option>
          </select>
        </label>
      </div>
      {problem.jobId && (
        <p className="mt-1 text-sm text-red-700" role="alert">
          {problem.jobId}
        </p>
      )}
      {elsewhere && (
        <div className="row mt-3">
          <label htmlFor={id('location')}>
            Where
            <input
              id={id('location')}
              className="form-input"
              maxLength={200}
              placeholder="The yard, a training course…"
              value={day.location ?? ''}
              onChange={(e) =>
                onChange({
                  location: e.target.value === '' ? null : e.target.value,
                })
              }
            />
          </label>
        </div>
      )}

      {showMore && activeLeave.length > 0 && (
        <div className="row mt-3">
          <label htmlFor={id('leave')}>
            Leave
            <select
              id={id('leave')}
              className="form-input"
              value={day.leaveTypeId ?? ''}
              onChange={(e) => {
                const leaveTypeId = e.target.value === '' ? null : e.target.value;
                onChange({
                  leaveTypeId,
                  leaveMinutes: leaveTypeId === null ? null : day.leaveMinutes,
                  // A whole day off was not spent on the job the schedule
                  // pre-filled; leaving it there puts a job number on a day
                  // of leave in the bookkeeper's export.
                  ...(leaveTypeId && !hasWindow && day.jobId === stored.jobId ? { jobId: null } : {}),
                });
              }}
            >
              <option value="">No leave</option>
              {activeLeave.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          {day.leaveTypeId && (
            <label htmlFor={id('leave-hours')}>
              Leave hours {!hasWindow && <span className="muted">(blank: whole day)</span>}
              <input
                id={id('leave-hours')}
                type="number"
                inputMode="decimal"
                min={0.25}
                max={24}
                step={0.25}
                className="form-input"
                aria-invalid={problem.leaveMinutes ? true : undefined}
                value={day.leaveMinutes === null ? '' : String(day.leaveMinutes / 60)}
                onChange={(e) =>
                  onChange({
                    leaveMinutes:
                      e.target.value === '' ? null : Math.max(1, Math.round(Number(e.target.value) * 60)),
                  })
                }
              />
            </label>
          )}
        </div>
      )}
      {problem.leaveMinutes && (
        <p className="mt-1 text-sm text-red-700" role="alert">
          {problem.leaveMinutes}
        </p>
      )}

      {showMore && activeAllowances.length > 0 && (
        <fieldset className="mt-3">
          <legend className="form-label">Allowances</legend>
          <div className="flex flex-wrap gap-x-4">
            {activeAllowances.map((t) => {
              const checked = day.allowanceTypeIds.includes(t.id);
              return (
                <label key={t.id} className="flex min-h-11 items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={checked}
                    onChange={() =>
                      onChange({
                        allowanceTypeIds: checked
                          ? day.allowanceTypeIds.filter((a) => a !== t.id)
                          : [...day.allowanceTypeIds, t.id],
                      })
                    }
                  />
                  {t.name}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {showMore && (
        <div className="row mt-3">
          <label htmlFor={id('note')}>
            Note <span className="muted">(optional)</span>
            <input
              id={id('note')}
              className="form-input"
              maxLength={1000}
              value={day.note ?? ''}
              onChange={(e) =>
                onChange({
                  note: e.target.value === '' ? null : e.target.value,
                })
              }
            />
          </label>
        </div>
      )}

      {(onCopy || !showMore) && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
          {!showMore && (
            <button type="button" className="row-action min-h-11" onClick={() => setMore(true)}>
              Add leave, an allowance or a note
            </button>
          )}
          {onCopy && (
            <button type="button" className="row-action min-h-11" onClick={onCopy}>
              Same as the day before
            </button>
          )}
        </div>
      )}
    </section>
  );
}
